import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  generateMonthlyExport,
  getBulkCount,
  getBulkList,
  getMonthlyExportStatus,
  isSummaryTableAvailable,
} from '../../../api/statements';
import { Layout, Loader, Pagination } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { fetchDashboardFilterData, reports } from '../../../utils/constant';
import { apiErrorHandler, errorHandler, successHandler } from '../../../utils/handlers';
import NewCommonFilters from '../../dashboard/newCommonFilters';
import DownloadModal from '../../reports/downloadModal/downloadModal';
import EmailModal from '../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../reports/toolbar/toolbar';
import CommonFilter from '../commonFilter';
import BulkStatementTable from './bulkStatementTable';
import DetailedTable from './detailedTable';
import MonthlyReportPopup from './MonthlyReportPopup';

const BulkStatements = () => {
  const [data, setData] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({});
  const { page, limit, setPage, setLimit } = usePagination();
  const [totalCount, setTotalCount] = useState(null);
  const [showDetailedTable, setShowDetailedTable] = useState(false);
  const [rowData, setRowData] = useState({});
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [checkHeader, setCheckHeader] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectDue, setSelectDue] = useState(true);
  const [bulkIdsPayload, setBulkIdsPayload] = useState([]);
  const [bulkIdsPayloadUnSelected, setBulkIdsPayloadUnSelected] = useState([]);
  const [rowsChecked, setRowsChecked] = useState({});
  const [rowsUnChecked, setRowsUnChecked] = useState({});
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  const [statusPayload, setPayloadStatus] = useState(['no status']);
  const [description, setDescription] = useState('');
  const [generateType, setGenerateType] = useState('');
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  useEffect(() => {
    if (initialRenderDone && payload?.startDate && payload?.endDate) {
      generateReport(payload, 'globalFilterChange');
    }
    setInitialRenderDone(true);
  }, [filterData]);
  useEffect(() => {
    fetchStatusData();
  }, []);
  async function fetchStatusData(payloadData) {
    try {
      const response = await getMonthlyExportStatus({
        payloadData,
      });
      const res = response.data.data.result;
      if (res.length !== 0) {
        setShowStatusMessage(true);
        const failed = response.data.data.error.length;
        let allCompleted = true;
        for (let index = 0; index < res.length; index += 1) {
          if (res[index].status === 'Completed') {
            setPayloadStatus(response);
          } else {
            allCompleted = false;
            setPayloadStatus(response);
          }
        }
        if (allCompleted) {
          setShowStatusMessage(false);
        } else {
          setShowStatusMessage(true);
        }
        if (failed !== 0) {
          setMessage('Error Occured');
          setLoading(false);
        }
      } else {
        setShowStatusMessage(false);
        setLoading(false);
        setPayloadStatus(response);
      }
      setLoading(false);
    } catch (err) {
      apiErrorHandler(err);
      setLoading(false);
    }
  }
  const showOpiDetails = (selectedRowData) => {
    setRowData(selectedRowData);
    setShowDetailedTable(true);
  };

  const onCancel = () => {
    setShowStatusPopup(false);
  };
  const onConfirm = () => {
    setShowStatusPopup(false);
    if (generateType && generateType !== 'doNotGenerate') {
      const payloadData = payload;
      payloadData.generateType = generateType;
      monthlyExport(payloadData);
      setGenerateType(null);
    }
  };
  const onGenerateTypeChange = (value) => {
    setGenerateType(value);
  };
  const toggleStatusPopup = () => setShowStatusPopup((s) => !s);
  const toggleEmailModal = () => {
    if ((!selectAll && bulkIdsPayload?.length > 0 && data?.length > 0) || selectAll) {
      setIsEmailModalOpen(!isEmailModalOpen);
    } else if (data?.length) {
      errorHandler('Please select rows');
    }
  };

  const toggleDownloadModal = () => {
    if ((!selectAll && bulkIdsPayload?.length > 0 && data?.length > 0) || selectAll) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    } else if (data?.length) {
      errorHandler('Please select rows');
    }
  };

  const onDownloadClick = () => {
    successHandler({
      message: {
        message:
          'Reports generation job is in progress. You will receive an email with downloadable link on your email',
        title: 'Success',
      },
    });
  };

  const checkSummaryTable = async (payloadData) => {
    try {
      setLoading(true);
      const response = await isSummaryTableAvailable(payloadData);
      setLoading(false);
      if (response?.data && response.data.error === 'TABLE_NOT_AVAILABLE') {
        setGenerateType(null);
        setPayload(payloadData);
        setShowStatusPopup(true);
        setMessage('Summary Table Status');
        setDescription(
          'Summary table is not availabe for this month, please select one of the following options',
        );
      } else if (response?.data?.success && response.data.status === 'SCHEDULED_ALREADY') {
        setShowStatusPopup(true);
        setMessage('Monthly Generation');
        setDescription(response.data.data);
      } else if (response?.data?.success) {
        monthlyExport(payloadData);
      }
    } catch (_err) {
      setLoading(false);
    }
  };

  const monthlyExport = async (payloadData) => {
    try {
      setLoading(true);
      const response = await generateMonthlyExport(payloadData);
      setLoading(false);
      setShowStatusPopup(true);
      setMessage('Monthly Generation');
      setDescription(response.data.data);
    } catch (_err) {
      setLoading(false);
    }
  };
  const monthlyExportStatus = async (payloadData) => {
    try {
      const response = await getMonthlyExportStatus(payloadData);
      setPayloadStatus(response);
      setMessage('Status');
      setShowStatusPopup(true);
    } catch (_err) {
      setMessage('Error');
    }
  };
  const generateReport = async (payloadData, type) => {
    if (type === 'monthlyExport') {
      checkSummaryTable(payloadData);
      return;
    }
    if (type === 'monthlyExportStatus') {
      monthlyExportStatus(payloadData);
      return;
    }

    if (type === 'search' || type === 'globalFilterChange' || type === 'click') {
      setPage(1);
      setLimit(10);
      setSlStartNumber(0);
      setBulkIdsPayload([]);
      setRowsChecked({});
      setSelectAll(false);
      setBulkIdsPayloadUnSelected([]);
      setRowsUnChecked({});
      setCheckHeader(false);
    }
    if (type === 'click') {
      setSelectDue(true);
    }
    setApplyFilters(false);
    setLoading(true);
    try {
      setPayload({
        ...payloadData,
        ...fetchDashboardFilterData(filterData),
      });
      const response = await getBulkList({
        ...payloadData,
        limit:
          type === 'search' || type === 'click' || type === 'globalFilterChange'
            ? payloadData?.limit
            : limit,
        page:
          type === 'search' || type === 'click' || type === 'globalFilterChange'
            ? payloadData?.page
            : page,
        ...fetchDashboardFilterData(filterData),
      });

      const countPayload = payloadData;
      delete countPayload.limit;
      delete countPayload.page;
      const Countresponse = await getBulkCount({
        ...countPayload,
        ...fetchDashboardFilterData(filterData),
      });
      setTotalCount(Countresponse?.data?.data?.totalCount);
      setData(response?.data?.data);
      setShowTable(true);
      setLoading(false);
    } catch (_err) {
      setLoading(false);
    }
  };
  const debounceApiCall = useCallback(
    debounce((APIpayload) => {
      if (moment(APIpayload.startDate).isValid() && moment(APIpayload.endDate).isValid()) {
        generateReport(APIpayload, 'search');
      }
    }, 2000),
    [],
  );

  useEffect(() => {
    if (applyFilters) {
      setSlStartNumber(page * limit - limit);
      generateReport(payload, 'pagination');
    }
  }, [page, limit]);

  useEffect(() => {
    if (!selectAll) {
      checkAllChecked();
    }
  }, [data]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const renderModals = () => {
    return (
      <>
        {isEmailModalOpen && (
          <EmailModal
            isOpen={isEmailModalOpen}
            toggle={toggleEmailModal}
            reportType={reports.BULK}
            apiValues={{
              startDate: payload?.startDate,
              endDate: payload?.endDate,
            }}
            bulkIdsPayload={bulkIdsPayload}
            bulkIdsPayloadUnSelected={bulkIdsPayloadUnSelected}
            selectAll={selectAll}
            selectDue={selectDue}
            onSelectDueClick={onSelectDueClick}
            newFilters
          />
        )}

        {isDownloadModalOpen && (
          <DownloadModal
            isOpen={isDownloadModalOpen}
            toggle={toggleDownloadModal}
            reportType={reports.BULK}
            urlParams={{
              startDate: payload?.startDate,
              endDate: payload?.endDate,
            }}
            onDownloadClick={onDownloadClick}
            bulkIdsPayload={bulkIdsPayload}
            bulkIdsPayloadUnSelected={bulkIdsPayloadUnSelected}
            selectAll={selectAll}
            selectDue={selectDue}
            onSelectDueClick={onSelectDueClick}
            willReceiveFileInEmail
            newFilters
          />
        )}
      </>
    );
  };

  const checkAllChecked = () => {
    const tempRows = rowsChecked;
    let allChecked = true;
    for (let index = 0; index < data?.length; index += 1) {
      if (!tempRows[`${data[index]?.bills}_${data[index]?.onePortalId}`]) {
        allChecked = false;
        break;
      }
    }
    if (allChecked) {
      setCheckHeader(true);
    } else {
      setCheckHeader(false);
    }
  };
  const checkItemIndexFromArray = (array, item) => {
    let itemIndex = -1;
    for (let index = 0; index < array.length; index += 1) {
      if (array[index].bills === item.bills && array[index].onePortalId === item.onePortalId) {
        itemIndex = index;
      }
    }

    return itemIndex;
  };
  const onHeaderCheckedChange = (event) => {
    setCheckHeader(event.target.checked);
    const tempRows = rowsChecked;

    let bulkIdsPayloadValues = [...bulkIdsPayload];
    if (event?.target?.checked) {
      data.forEach((rowDataValue, index) => {
        const itemIndex = checkItemIndexFromArray(bulkIdsPayloadValues, rowDataValue);
        if (itemIndex === -1) {
          bulkIdsPayloadValues = [
            ...bulkIdsPayloadValues,
            {
              rowIndex: index,
              email: rowDataValue?.email,
              onePortalId: rowDataValue?.onePortalId,
              bills: rowDataValue?.bills,
            },
          ];
        }
        tempRows[`${rowDataValue?.bills}_${rowDataValue?.onePortalId}`] = event?.target?.checked;
      });
    } else {
      data.forEach((rowDataValue) => {
        const uniqueId = `${rowDataValue?.bills}_${rowDataValue?.onePortalId}`;
        if (tempRows[uniqueId]) {
          tempRows[uniqueId] = false;
          const itemIndex = checkItemIndexFromArray(bulkIdsPayloadValues, rowDataValue);
          if (itemIndex > -1) {
            bulkIdsPayloadValues.splice(itemIndex, 1);
          }
        }
      });
    }
    setRowsChecked(tempRows);
    setBulkIdsPayload(bulkIdsPayloadValues);
  };
  const findData = (bills, onePortalId) => {
    let selectedData = {};
    for (let index = 0; index < data?.length; index += 1) {
      if (data[index].bills === bills && data[index].onePortalId === onePortalId) {
        selectedData = data[index];
        break;
      }
    }
    return selectedData;
  };
  const selectedSeachedRows = () => {
    if (searchText) {
      data.forEach((rowDataValue) => {
        const { bills } = rowDataValue;
        const { onePortalId } = rowDataValue;
        const uniqueId = `${bills}_${onePortalId}`;
        const itemIndex = checkItemIndexFromArray(bulkIdsPayload, rowDataValue);
        let bulkIdsPayloadValues = [...bulkIdsPayload];
        if (itemIndex === -1) {
          const tempRows = rowsChecked;
          tempRows[uniqueId] = true;
          const selectedRow = findData(bills, onePortalId);
          bulkIdsPayloadValues = [
            ...bulkIdsPayloadValues,
            {
              email: selectedRow?.email,
              onePortalId: selectedRow?.onePortalId,
              bills: selectedRow?.bills,
            },
          ];
          setBulkIdsPayload(bulkIdsPayloadValues);
        } else {
          setBulkIdsPayload(bulkIdsPayloadValues);
        }
      });
    }
  };
  const onSelectAllClick = () => {
    if (selectAll) {
      setRowsChecked({});
      setBulkIdsPayload([]);
      setBulkIdsPayloadUnSelected([]);
      setRowsUnChecked({});
      setCheckHeader(false);
    } else {
      setRowsChecked({});
      setBulkIdsPayload([]);
      setCheckHeader(true);
      selectedSeachedRows();
    }
    setSelectAll(!selectAll);
  };

  const onSelectDueClick = () => {
    setRowsChecked({});
    setBulkIdsPayload([]);
    setBulkIdsPayloadUnSelected([]);
    setRowsUnChecked({});
    setSelectAll(false);
    setSelectDue(!selectDue);
    generateReport({ ...payload, dueSelected: !selectDue }, 'search');
  };

  const rowCheckChanged = (event, bills, onePortalId) => {
    const uniqueId = `${bills}_${onePortalId}`;
    if (selectAll) {
      const tempRows = rowsUnChecked;
      tempRows[uniqueId] = !event?.target?.checked;
      setRowsUnChecked({ ...rowsUnChecked, ...tempRows });

      let bulkIdsPayloadValues = [...bulkIdsPayloadUnSelected];
      if (event?.target?.checked) {
        bulkIdsPayloadValues = bulkIdsPayloadValues.filter((value) => {
          return value !== onePortalId;
        });
        setBulkIdsPayloadUnSelected(bulkIdsPayloadValues);
      } else {
        const selectedRow = findData(bills, onePortalId);
        bulkIdsPayloadValues = [...bulkIdsPayloadValues, selectedRow?.onePortalId];
        setBulkIdsPayloadUnSelected(bulkIdsPayloadValues);
        if (searchText) {
          // if searchText is available then selectAll will not work
          const bulkIdsPayloadSelected = [...bulkIdsPayload];
          const itemIndex = checkItemIndexFromArray(bulkIdsPayloadSelected, {
            bills,
            onePortalId,
          });
          if (itemIndex > -1) {
            bulkIdsPayloadSelected.splice(itemIndex, 1);
          }
          setBulkIdsPayload(bulkIdsPayloadSelected);
        }
      }
    } else {
      const tempRows = rowsChecked;
      tempRows[uniqueId] = event?.target?.checked;
      setRowsChecked({ ...rowsChecked, ...tempRows });
      checkAllChecked();
      let bulkIdsPayloadValues = [...bulkIdsPayload];
      if (event?.target?.checked) {
        const selectedRow = findData(bills, onePortalId);
        bulkIdsPayloadValues = [
          ...bulkIdsPayloadValues,
          {
            email: selectedRow?.email,
            onePortalId: selectedRow?.onePortalId,
            bills: selectedRow?.bills,
          },
        ];
      } else {
        const itemIndex = checkItemIndexFromArray(bulkIdsPayloadValues, {
          bills,
          onePortalId,
        });
        if (itemIndex > -1) {
          bulkIdsPayloadValues.splice(itemIndex, 1);
        }
      }
      setBulkIdsPayload(bulkIdsPayloadValues);
    }
  };
  return (
    <>
      <Layout>
        <div className="bulk_container layout-in-div">
          <div className={showDetailedTable ? 'bult-d-none' : ''}>
            <NewCommonFilters />
            <CommonFilter
              title="Bulk Customer Statement"
              generateReport={generateReport}
              debounceApiCall={debounceApiCall}
              type="bulk"
              selectDue={selectDue}
              onSelectDueClick={onSelectDueClick}
              searchText={searchText}
              fromDate={fromDate}
              toDate={toDate}
              setSearchText={setSearchText}
              setFromDate={setFromDate}
              setToDate={setToDate}
              showStatusMessage={showStatusMessage}
              message={message}
            />
            {showTable && (
              <>
                <Toolbar
                  optionOneLabel="Email Report"
                  optionOneIcon="MdOutlineEmail"
                  onOptionOneClick={toggleEmailModal}
                  optionTwoLabel="Download Report"
                  optionTwoIcon="FiDownload"
                  onOptionTwoClick={toggleDownloadModal}
                  isTableDataPresent={data?.length}
                  requiredSelectAll
                  onSelectAllClick={onSelectAllClick}
                  selectAll={selectAll}
                  totalCount={totalCount}
                  requiredSelectDue
                  selectDue={selectDue}
                  onSelectDueClick={onSelectDueClick}
                />
                {data && data?.length > 0 ? (
                  <>
                    <BulkStatementTable
                      slNo={slStartNumber}
                      data={data}
                      onPurchaserOpiClick={showOpiDetails}
                      bulkIdsPayload={bulkIdsPayload}
                      setBulkIdsPayload={setBulkIdsPayload}
                      checkAllChecked={checkAllChecked}
                      onHeaderCheckedChange={onHeaderCheckedChange}
                      rowCheckChanged={rowCheckChanged}
                      checkHeader={checkHeader}
                      rowsChecked={rowsChecked}
                      selectAll={selectAll}
                      selectDue={selectDue}
                      rowsUnChecked={rowsUnChecked}
                    />
                    {totalCount ? (
                      <div className="pagination-araging-report">
                        <Pagination
                          totalResults={totalCount}
                          page={page}
                          limit={limit}
                          setPage={(pageNo) => {
                            setPage(pageNo);
                            setApplyFilters(true);
                          }}
                          setLimit={(rowlimit) => {
                            setLimit(rowlimit);
                            setApplyFilters(true);
                          }}
                          limitOptions={[10, 15, 20]}
                        />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="loader-empty">
                    <LoaderComponent />
                  </div>
                )}
              </>
            )}
          </div>
          {showDetailedTable && (
            <div className="detailed_table">
              <DetailedTable
                setShowDetailedTable={setShowDetailedTable}
                payload={payload}
                rowData={rowData}
              />
            </div>
          )}
        </div>
        <MonthlyReportPopup
          isOpen={showStatusPopup}
          toggle={toggleStatusPopup}
          onConfirm={onConfirm}
          onCancel={onCancel}
          message={message}
          confirmBtnText="OK"
          statusPayload={statusPayload}
          description={description}
          generateType={generateType}
          setGenerateType={onGenerateTypeChange}
        />
      </Layout>
      {renderModals()}
    </>
  );
};

export default BulkStatements;
